import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal } from "./Reveal";

const faqs = [
    {
        q: "What exactly is RapidClick and who is it for?",
        a: "RapidClick is an Android automation tool for Rapido and Porter drivers in India. It reads incoming order details — price, distances, addresses — matches them against your rules, and auto-accepts good orders or rejects bad ones in under 500 milliseconds.",
    },
    {
        q: "Which delivery platforms does RapidClick work with?",
        a: "Rapido and Porter are fully supported — auto-accept, auto-reject, distance and area filters, plus order history tracking. Support for more platforms is in development.",
    },
    {
        q: "Is RapidClick available on iPhone / iOS?",
        a: "RapidClick works on Android 8.0 and above. It is not available for iOS.",
    },
    {
        q: "Is RapidClick free to use?",
        a: "RapidClick is free to download and every new user automatically receives a 24-hour Free Trial to test all of our features! After your trial ends, you can choose an affordable Monthly, Yearly, or Lifetime Premium subscription to continue automating your tasks without limits.",
    },
    {
        q: "How does the auto-accept feature actually work?",
        a: "When an order notification arrives, RapidClick wakes the screen, reads the price, distances and addresses, and checks them against your rules. Matching orders get an instant Accept tap — others are rejected. The full process takes under 500 milliseconds.",
    },
    {
        q: "How do I set up area-based filtering?",
        a: "Open Drop Area, tap 'Pick on Map', place a pin on your preferred zone and choose a 5–25 km radius. RapidClick then accepts only orders dropping inside that zone. You can save up to 3 locations for quick switching.",
    },
    {
        q: "What's the difference between 'Go To' and 'Stay In' modes?",
        a: "Go To accepts orders heading into your chosen area — useful when you want to travel somewhere specific. Stay In accepts only orders that keep you inside your area.",
    },
    {
        q: "What permissions does RapidClick need and why?",
        a: "- Accessibility Service — reads order popups and taps Accept or Reject. The core permission.\n- Notification Listener — detects new orders and wakes the screen.\n- Location (optional) — only to center the map in the area filter.\n- Display Over Apps — shows accepted/rejected confirmation overlays.\nAll permissions can be revoked anytime in Android settings.",
    },
    {
        q: "Is it safe to use? Will my account get banned?",
        a: "RapidClick uses Android's standard Accessibility framework — no root, no app modification, no code injection. Thousands of drivers use it daily. However, automation is a gray area in platform policies, so use it responsibly and at your own discretion.",
    },
    {
        q: "Does RapidClick access my personal data or messages?",
        a: "No. The app responds only to Rapido and Porter screens and ignores everything else. It cannot read messages, passwords, banking apps or contacts. All order data stays on your device and is never uploaded.",
    },
    {
        q: "Does RapidClick drain my battery?",
        a: "Minimal impact — typically under 3–5% per day. The app sleeps until a Rapido or Porter order screen appears, with no GPS tracking or background polling.",
    },
    {
        q: "The app is not auto-accepting orders. What should I do?",
        a: "- Make sure the service shows 'Active' in RapidClick.\n- Check Accessibility and Notification access are both ON.\n- Set battery to 'Unrestricted' for RapidClick (especially Xiaomi, Realme, Oppo, Vivo).\n- Lock the app in the recent apps tray.\n- Widen your distance/area rules temporarily to test.\nStill stuck? Use the in-app diagnostic or contact Help & Support.",
    },
    {
        q: "Which Android phones are supported?",
        a: "Any phone running Android 8.0 or higher — Samsung, Xiaomi/Redmi/POCO, Realme, Oppo, Vivo, OnePlus, Motorola, Nokia and Pixel are all tested. A built-in setup wizard handles aggressive battery settings on MIUI, ColorOS and FuntouchOS.",
    },
    {
        q: "Can I use RapidClick on multiple phones?",
        a: "Yes! Your RapidClick Premium subscription is securely linked to your Google Account, not just your physical phone. You can log into any of your devices with the same account and instantly access your Premium features.",
    },
    {
        q: "How do I contact support or report a bug?",
        a: "Fastest is in-app: Menu > Help & Support. You can also email hello@rapidclick.in (24-hour response on business days) or join our Telegram community. For bugs, include your phone model, Android version and a screenshot.",
    },
];

const VISIBLE_COUNT = 4;

const AnswerBody = ({ text }) => {
    const blocks = [];
    let list = [];
    text.split("\n").forEach((line) => {
        if (line.startsWith("- ")) {
            list.push(line.slice(2));
        } else {
            if (list.length) {
                blocks.push({ ul: list });
                list = [];
            }
            blocks.push({ p: line });
        }
    });
    if (list.length) blocks.push({ ul: list });
    return blocks.map((b, i) =>
        b.ul ? (
            <ul key={i} className="list-disc space-y-1.5 pl-5">
                {b.ul.map((li, j) => (
                    <li key={j}>{li}</li>
                ))}
            </ul>
        ) : (
            <p key={i}>{b.p}</p>
        ),
    );
};

export default function FAQ() {
    const [open, setOpen] = useState(0);
    const [showAll, setShowAll] = useState(false);
    const list = showAll ? faqs : faqs.slice(0, VISIBLE_COUNT);

    return (
        <section id="faq" data-testid="faq-section" className="pb-16 md:pb-32">
            <div className="mx-auto max-w-4xl px-6 lg:px-10">
                <Reveal>
                    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand">
                        FAQ
                    </p>
                    <h2 className="mt-4 font-display text-4xl font-bold tracking-tight text-ink md:text-6xl">
                        Questions? Answered.
                    </h2>
                    <p className="mt-5 max-w-lg text-base leading-relaxed text-mute">
                        Everything captains usually ask before installing RapidClick.
                    </p>
                </Reveal>

                <div className="mt-12">
                    {list.map((f, i) => (
                        <Reveal key={f.q} delay={Math.min(i, 3) * 0.06}>
                            <div
                                className="border-t border-line last:border-b"
                                data-testid={`faq-item-${i + 1}`}
                            >
                                <button
                                    data-testid={`faq-question-${i + 1}`}
                                    onClick={() => setOpen(open === i ? -1 : i)}
                                    className="group flex w-full items-center justify-between gap-6 py-6 text-left"
                                >
                                    <span className="font-display text-lg font-bold text-ink transition-colors group-hover:text-brand md:text-xl">
                                        {f.q}
                                    </span>
                                    <span
                                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
                                            open === i
                                                ? "rotate-45 border-brand bg-brand text-white"
                                                : "border-line text-mute group-hover:border-brand/40"
                                        }`}
                                    >
                                        <Plus size={15} />
                                    </span>
                                </button>
                                <AnimatePresence initial={false}>
                                    {open === i && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                            className="overflow-hidden"
                                        >
                                            <div className="space-y-3 pb-6 pr-4 text-sm leading-relaxed text-mute md:pr-16">
                                                <AnswerBody text={f.a} />
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal className="mt-10 text-center">
                    <button
                        data-testid="faq-toggle-all-btn"
                        onClick={() => {
                            setShowAll(!showAll);
                            setOpen(-1);
                        }}
                        className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-7 py-3.5 text-sm font-bold text-ink transition-all duration-300 hover:border-ink/40 hover:scale-[1.03]"
                    >
                        {showAll ? "Show fewer questions" : `Show all ${faqs.length} questions`}
                    </button>
                </Reveal>
            </div>
        </section>
    );
}
